import {
    computed,
    inject,
    InjectionToken,
    Provider,
    signal,
    Signal
} from "@angular/core";
import { CanActivateFn, Route } from "@angular/router";
import { AuthenticationService } from "./common/services/authentication/authentication.service";

export type PermissionFn = () => Signal<boolean>;

type ApplicableType = boolean | PermissionFn;

type RolePermission = {
    role: string;
    canEdit: Signal<boolean>;
    canView: Signal<boolean>;
};

export type RolePermissionParameters = {
    role: string;
    canEdit?: ApplicableType;
    canView?: ApplicableType;
};

export const PERMISSIONS = new InjectionToken<RolePermission[]>("permissions");

const hasAnyApplicableRole = (roleFilter?: (permission: RolePermission) => Signal<boolean>): Signal<boolean> => {
    const permissions = inject(PERMISSIONS);
    const authenticationService = inject(AuthenticationService);

    return computed(() => {
        const applicableRoles = permissions
            .filter(permission => roleFilter?.(permission)())
            .map(permission => permission.role);

        return authenticationService.hasAnyRole(applicableRoles);
    });
};

const permissionGuard: CanActivateFn = () => canView()();

type RoleProviderFn = (...roles: RolePermissionParameters[]) => Provider;

const mapApplicableTypeToSignal = (applicable: ApplicableType | undefined, defaultValue: boolean): Signal<boolean> => {
    if (applicable === undefined) {
        return signal(defaultValue);
    }

    if (typeof applicable === "function") {
        return applicable();
    }

    return signal(applicable);
};

const mapParametersToPermissions = (roles: RolePermissionParameters[]): RolePermission[] => roles.map(role => {
    const canEdit = mapApplicableTypeToSignal(role.canEdit, false);
    const canView = mapApplicableTypeToSignal(role.canView, true);

    return {
        ...role,
        canView,
        canEdit
    };
});

export const providePermissions: RoleProviderFn = (...roles: RolePermissionParameters[]) => ({
    provide: PERMISSIONS,
    useFactory: () => mapParametersToPermissions(roles)
});

export const updatePermissions: RoleProviderFn = (...permissions: RolePermissionParameters[]) => ({
    provide: PERMISSIONS,
    useFactory: () => {
        const current = inject(PERMISSIONS, {
            skipSelf: true,
            optional: true
        }) ?? [];

        const notUpdated = current.filter(p => !permissions.some(up => up.role === p.role));
        return [
            ...notUpdated,
            ...mapParametersToPermissions(permissions)
        ];
    }
});

export const withPermissions = (route: Route & { permissions: RolePermissionParameters[] }): Route => {
    const {
        providers, data, canActivate, permissions, ...rest
    } = route;

    return {
        ...rest,
        providers: [
            providePermissions(...permissions),
            ...(providers ?? [])
        ],
        data: {
            ...data,
            requiredRoles: permissions.map(p => p.role),
            editRoles: permissions
                .filter(p => p.canEdit)
                .map(p => p.role)
        },
        canActivate: [
            ...(canActivate ?? []),
            permissionGuard
        ]
    };
};

export const canEdit = () => hasAnyApplicableRole(permission => permission.canEdit);

export const canView = () => hasAnyApplicableRole(permission => permission.canView);
